import React, { useState, useEffect } from 'react';
import { DollarSign, Send, History, Wallet, Clock } from 'lucide-react';
import { ApiService, Company, Transaction } from '../services/api';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { useAuth } from '../providers/AuthProvider';
import { usePagination } from '../hooks/usePagination';
import toast from 'react-hot-toast';
import TransactionsTable from '../components/tables/TransactionsTable';
import SendMoneyModalWrapper from '../components/modals/SendMoneyModalWrapper';
import TransactionDetailsModal from '../components/modals/TransactionDetailsModal';

// Using types from ApiService

const UserDashboard: React.FC = () => {
  const [company, setCompany] = useState<Company | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showTransactionDetails, setShowTransactionDetails] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [availableCompanies, setAvailableCompanies] = useState<{company: Company, exchangeRate: number}[]>([]);
  const [isSending, setIsSending] = useState(false);
  // const [, setShowProcessing] = useState(false);
  
  // Sorting state
  const [sortBy, setSortBy] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Date filtering state
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  
  // Pagination hook
  const transactionsPagination = usePagination(transactions, { itemsPerPage: 10 });
  
  const { connected, publicKey, signTransaction } = useWallet();
  const { setVisible } = useWalletModal();
  const { user } = useAuth();

  // Sort handler
  const handleSort = (field: string, order: 'asc' | 'desc') => {
    setSortBy(field);
    setSortOrder(order);
  };

  // Date filter handlers
  const handleStartDateChange = (date: string | undefined) => {
    setStartDate(date || '');
  };

  const handleEndDateChange = (date: string | undefined) => {
    setEndDate(date || '');
  };

  const handleClearDateFilter = () => {
    setStartDate('');
    setEndDate('');
  };

  useEffect(() => {
    fetchUserData();
  }, [user]); // Only refetch when user changes (login/logout)

  useEffect(() => {
    if (company) {
      fetchUserData();
    }
  }, [sortBy, sortOrder, startDate, endDate]);

  useEffect(() => {
    if (company) {
      fetchAvailableCompanies();
    }
  }, [company]); // Refetch available companies when company data changes

  const fetchUserData = async () => {
    try {
      // Get user's company directly from their profile (no wallet connection required)
      const companyData = await ApiService.getUserCompany();
      console.log('UserDashboard - companyData:', companyData);
      
      if (companyData) {
        setCompany(companyData);
        
        // Fetch transactions by company ID
        try {
          const transactionsData = await ApiService.getTransactionsByCompanyId(
            companyData.id, 
            undefined, 
            sortBy || undefined, 
            sortOrder,
            startDate && startDate.trim() !== '' ? startDate : undefined,
            endDate && endDate.trim() !== '' ? endDate : undefined
          );
          setTransactions(transactionsData);
        } catch (error) {
          console.error('Failed to fetch transactions:', error);
          setTransactions([]);
        }
      } else {
        // No company found for this user
        setCompany(null);
        setTransactions([]);
      }
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      toast.error('Failed to load dashboard data');
      setCompany(null);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableCompanies = async () => {
    try {
      if (company?.companyWallet) {
        // Get allowed companies for the user's company wallet
        const allowedCompanies = await ApiService.getAllowedCompaniesForSender(company.companyWallet);
        setAvailableCompanies(allowedCompanies);
      } else {
        // Fallback to all companies if no wallet found
        const companies = await ApiService.getAllCompaniesForUser();
        // Convert Company[] to {company: Company, exchangeRate: number}[]
        const companiesWithExchangeRate = companies.map(company => ({
          company,
          exchangeRate: 1 // Default exchange rate
        }));
        setAvailableCompanies(companiesWithExchangeRate);
      }
    } catch (error) {
      console.error('Failed to fetch available companies:', error);
      setAvailableCompanies([]);
    }
  };

  const handleViewTransactionDetails = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setShowTransactionDetails(true);
  };

  const handleViewTransactionOnSolscan = (transaction: Transaction) => {
    if (transaction.solanaSignature) {
      const solscanUrl = `https://solscan.io/tx/${transaction.solanaSignature}?cluster=devnet`;
      window.open(solscanUrl, '_blank');
    } else {
      toast.error('Transaction signature not available');
    }
  };

  const handleSendMoney = async (data: { receiverCompanyId: string; amount: string }) => {
    if (!company) return;

    if (!connected || !publicKey || !signTransaction) {
      toast.error('Please connect your wallet first');
      setVisible(true);
      return;
    }

    setIsSending(true);
    try {
      const amountInCents = Math.round(parseFloat(data.amount) * 100);
      console.log('amountInCents', amountInCents);
      // Find the selected company
      // const selectedCompany = availableCompanies.find(c => c.company.id === data.receiverCompanyId);
      // if (!selectedCompany) {
      //   toast.error('Selected company not found');
      //   return;
      // }

      // // Create wallet object for signing
      // const wallet = {
      //   publicKey,
      //   signTransaction,
      // };

      // const result = await ApiService.createHold(
      //   {
      //     receiverCompanyWallet: selectedCompany.company.companyWallet,
      //     amount: parseFloat(data.amount) * 100, // Convert to cents
      //   },
      //   wallet
      // );

      // if (result) {
      //   // toast.success('Hold created successfully!');
      //   setShowProcessing(true);
      //   setShowSendModal(false);
      //   fetchUserData(); // Refresh data
      // } else {
      //   toast.error('Failed to create hold');
      // }
    } catch (error: any) {
      setShowSendModal(false);
      fetchUserData(); // Refresh data
      console.error('Failed to create hold:', error);
      toast.error(error.response?.data?.message || 'Failed to create hold');
    } finally {
      setIsSending(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount / 100);
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Show wallet connection notice but don't block access to dashboard
  const showWalletNotice = !connected || !publicKey;

  if (!company) {
    return (
      <div className="space-y-6">
        {/* Wallet Connection Notice */}
        {showWalletNotice && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <Wallet className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Wallet Not Connected
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    Connect your wallet to access full functionality and sign transactions.
                  </p>
                </div>
                <div className="mt-4">
                  <button
                    onClick={() => setVisible(true)}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-yellow-800 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                  >
                    <Wallet className="h-4 w-4 mr-2" />
                    Connect Wallet
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="text-center py-12">
          <Wallet className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-white">No company found</h3>
          <p className="mt-1 text-sm text-gray-400">
            {showWalletNotice 
              ? "Connect your wallet to check for your company account."
              : "No company account was found. Please contact support."
            }
          </p>
        </div>
      </div>
    );
  }

  if (company.status === 'PENDING') {
    return (
      <div className="space-y-6">
        {/* Wallet Connection Notice */}
        {showWalletNotice && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <Wallet className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Wallet Not Connected
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    Connect your wallet to access full functionality and sign transactions.
                  </p>
                </div>
                <div className="mt-4">
                  <button
                    onClick={() => setVisible(true)}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-yellow-800 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                  >
                    <Wallet className="h-4 w-4 mr-2" />
                    Connect Wallet
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="text-center py-12">
          <Clock className="mx-auto h-12 w-12 text-yellow-400" />
          <h3 className="mt-2 text-sm font-medium text-white">Account Pending Approval</h3>
          <p className="mt-1 text-sm text-gray-400">
            Your company account is pending admin approval. You'll be notified once it's approved.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Wallet Connection Notice */}
      {showWalletNotice && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <Wallet className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Wallet Not Connected
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  Connect your wallet to access full functionality and sign transactions.
                </p>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => setVisible(true)}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-yellow-800 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                >
                  <Wallet className="h-4 w-4 mr-2" />
                  Connect Wallet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Welcome, {company.companyName}</h1>
          <p className="text-sm sm:text-base text-gray-400 mt-1 sm:mt-0">Manage your settlements and transactions</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:space-x-3 sm:space-y-0 w-full sm:w-auto">
          <button
            onClick={() => setShowSendModal(true)}
            className="w-full sm:w-auto inline-flex items-center justify-center sm:justify-start px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#2777EF] hover:bg-[#0053E2]"
          >
            <Send className="h-4 w-4 mr-2" />
            Initiate Settlement
          </button>
        </div>
      </div>

      {/* Balance Card */}
      <div className="bg-[#182233] overflow-hidden shadow rounded-lg">
        <div className="p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <DollarSign className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Account Balance
                </dt>
                <dd className="text-3xl font-bold text-white">
                  {formatCurrency(company.valueAmount)}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-[#182233] overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Send className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Min Transaction
                  </dt>
                  <dd className="text-lg font-medium text-white">
                    {formatCurrency(company.minTransactionAmount)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-[#182233] overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DollarSign className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Max Transaction
                  </dt>
                  <dd className="text-lg font-medium text-white">
                    {formatCurrency(company.maxTransactionAmount)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-[#182233] overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <History className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Transactions
                  </dt>
                  <dd className="text-lg font-medium text-white">
                    {transactions.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="bg-[#182233] shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-white mb-4">
            Recent Transactions
          </h3>
          {transactions.length === 0 ? (
            <div className="text-center py-8">
              <History className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-white">No transactions yet</h3>
              <p className="mt-1 text-sm text-gray-400">
                Start by sending money to another company.
              </p>
            </div>
          ) : (
            <TransactionsTable
              transactions={transactionsPagination.paginatedData}
              onViewDetails={handleViewTransactionDetails}
              onViewOnSolscan={handleViewTransactionOnSolscan}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={handleStartDateChange}
              onEndDateChange={handleEndDateChange}
              onClearDateFilter={handleClearDateFilter}
              showDateFilter={true}
              currentPage={transactionsPagination.currentPage}
              totalPages={transactionsPagination.totalPages}
              totalItems={transactionsPagination.totalItems}
              itemsPerPage={transactionsPagination.itemsPerPage}
              onPageChange={transactionsPagination.setCurrentPage}
              onItemsPerPageChange={transactionsPagination.setItemsPerPage}
              showPagination={true}
            />
          )}
        </div>
      </div>

      {/* Modals */}
      <SendMoneyModalWrapper
        isOpen={showSendModal}
        onClose={() => setShowSendModal(false)}
        onSubmit={handleSendMoney}
        availableCompanies={availableCompanies.filter(c => c.company.id !== company?.id)}
        currentCompany={company}
        isLoading={isSending}
        // showProcessingModal={false}
      />

      <TransactionDetailsModal
        isOpen={showTransactionDetails}
        onClose={() => setShowTransactionDetails(false)}
        transaction={selectedTransaction}
        onViewOnSolscan={handleViewTransactionOnSolscan}
      />

    </div>
  );
};

export default UserDashboard;
